import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { Button } from '../../../../components/ui';
import { CheckCircleIcon } from '../../../../components/ui/Icons';

const formatDateTime = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString('el-GR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const MyManualsDetailsTab = ({
  selectedRecord,
  isPending,
  isAcknowledging,
  ackError,
  onAcknowledge,
}) => {
  const { theme } = useTheme();

  if (!selectedRecord) return null;

  if (selectedRecord.canView === false) {
    return (
      <div className="p-4 space-y-3 text-sm">
        <p className="text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
          This manual is assigned to you but its status is <strong>Not-Valid</strong>. You can
          acknowledge it only when it becomes Valid.
        </p>
        <dl className="grid grid-cols-2 gap-3">
          <div>
            <dt className={theme.text.secondary}>Code</dt>
            <dd className={theme.text.primary}>{selectedRecord.code || '—'}</dd>
          </div>
          <div>
            <dt className={theme.text.secondary}>Title</dt>
            <dd className={theme.text.primary}>{selectedRecord.title || '—'}</dd>
          </div>
        </dl>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4 overflow-y-auto flex-1">
      {isPending && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-3 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/80 dark:bg-amber-900/20">
          <p className={cn('text-sm flex-1', theme.text.primary)}>
            Please review this manual and its attachments, then acknowledge.
          </p>
          <Button
            type="button"
            variant="primary"
            onClick={onAcknowledge}
            disabled={isAcknowledging}
            className="shrink-0"
          >
            <CheckCircleIcon className="w-4 h-4 mr-1.5 inline" />
            {isAcknowledging ? 'Acknowledging…' : 'Acknowledge'}
          </Button>
        </div>
      )}

      {ackError && (
        <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 p-2 rounded">{ackError}</p>
      )}

      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
        <div>
          <dt className={theme.text.secondary}>Code</dt>
          <dd className={cn('font-medium', theme.text.primary)}>{selectedRecord.code || '—'}</dd>
        </div>
        <div>
          <dt className={theme.text.secondary}>Title</dt>
          <dd className={cn('font-medium', theme.text.primary)}>{selectedRecord.title || '—'}</dd>
        </div>
        <div>
          <dt className={theme.text.secondary}>Type</dt>
          <dd className={theme.text.primary}>{selectedRecord.manualTypeName || '—'}</dd>
        </div>
        <div>
          <dt className={theme.text.secondary}>Departments</dt>
          <dd className={theme.text.primary}>{selectedRecord.departmentLabels || '—'}</dd>
        </div>
        <div>
          <dt className={theme.text.secondary}>Manual status</dt>
          <dd className={theme.text.primary}>{selectedRecord.manualStatusLabel || '—'}</dd>
        </div>
        <div>
          <dt className={theme.text.secondary}>Your status</dt>
          <dd className={theme.text.primary}>{selectedRecord.viewStatusLabel || '—'}</dd>
        </div>
        <div>
          <dt className={theme.text.secondary}>Assigned</dt>
          <dd className={theme.text.primary}>{formatDateTime(selectedRecord.assignedAt)}</dd>
        </div>
        <div>
          <dt className={theme.text.secondary}>Acknowledged</dt>
          <dd className={theme.text.primary}>{formatDateTime(selectedRecord.viewedAt)}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className={theme.text.secondary}>Description</dt>
          <dd className={cn('whitespace-pre-wrap', theme.text.primary)}>
            {selectedRecord.description || '—'}
          </dd>
        </div>
      </dl>
    </div>
  );
};

export default MyManualsDetailsTab;
